import { Injectable } from '@angular/core';
import { LocalStorageKey, LocalStorageService } from './local-storage.service';

/**
 * Shared user identity info that can be propagated via URL params between flows/apps.
 * This intentionally lives in `shared` so no app needs to import another app's source code.
 *
 * Currently supported query params:
 * - `email`
 * - `stripeCustomerId`
 */
@Injectable({
  providedIn: 'root',
})
export class UserInfoService {
  private email?: string;
  private stripeCustomerId?: string;

  constructor(private storage: LocalStorageService) {
    // Best-effort hydrate from localStorage on startup (may fail in some environments).
    this.email = this.storage.get<string>(LocalStorageKey.email);
    this.stripeCustomerId = this.storage.get<string>(LocalStorageKey.stripeCustomerId);
  }

  /**
   * Reads user info from the provided URL search string (defaults to current window),
   * persists it to localStorage, and returns the resolved values.
   */
  hydrateFromUrl(search: string = window.location.search): {
    email?: string;
    stripeCustomerId?: string;
  } {
    const urlParams = new URLSearchParams(search);

    if (urlParams.has('email')) {
      // URLSearchParams has already decoded the value — a second decodeURIComponent would
      // throw URIError for valid percent-bearing addresses (e.g. foo%bar@example.com).
      const email = urlParams.get('email') || '';
      if (email) this.setEmail(email);
    }

    if (urlParams.has('stripeCustomerId')) {
      const stripeCustomerId = urlParams.get('stripeCustomerId') || '';
      if (stripeCustomerId) this.setStripeCustomerId(stripeCustomerId);
    }

    return { email: this.getEmail(), stripeCustomerId: this.getStripeCustomerId() };
  }

  setEmail(email: string): void {
    this.email = email;
    this.storage.set(LocalStorageKey.email, email);
  }

  getEmail(): string | undefined {
    // Prefer the in-memory value; fall back to storage if not initialized.
    if (this.email !== undefined) return this.email;
    this.email = this.storage.get<string>(LocalStorageKey.email);
    return this.email;
  }

  setStripeCustomerId(id: string): void {
    this.stripeCustomerId = id;
    this.storage.set(LocalStorageKey.stripeCustomerId, id);
  }

  getStripeCustomerId(): string | undefined {
    if (this.stripeCustomerId !== undefined) return this.stripeCustomerId;
    this.stripeCustomerId = this.storage.get<string>(LocalStorageKey.stripeCustomerId);
    return this.stripeCustomerId;
  }
}
